import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext, Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  Building2,
  CalendarCheck,
  CheckCircle2,
  Clock,
  FileText,
  Package,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Wind,
  Wrench,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from '../../api/axios';
import {
  manproQueryKeys,
  mapProviderBidToRow,
  providerBidService,
  providerOpportunityService,
  type PaginatedResponse,
  type ProviderBidDto,
  type ProviderOpportunityDto,
} from '../../api/manpro';
import { useFacilityManagementContext } from '../../features/facility/context/FacilityManagementContext';
import type { ApiResponseEnvelope, LayoutOutletContext } from '../../types/auth';

type SummaryData = {
  project?: {
    active_projects?: number;
    active?: number;
    physical_progress?: number;
    health_score?: number;
  };
  cleaning?: { today?: number };
  ac_maintenance?: { pending?: number };
  procurement?: { low_stock?: number };
  laundry?: { active?: number };
};

type ActivityItem = {
  task_name?: string;
  title?: string;
  team?: string;
  by?: string;
  time?: string;
  created_at?: string;
};

const getPaginatedRows = <T,>(payload?: PaginatedResponse<T> | T[] | null) => {
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? [];
};

const Dashboard = () => {
  const { userRole, permissions = [] } = useOutletContext<LayoutOutletContext>();
  const { technicianStats, isTechniciansLoading } = useFacilityManagementContext();
  const isProviderDashboard = userRole === 'provider';

  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: async () => {
      const response = await axios.get<ApiResponseEnvelope<SummaryData>>('/v1/admin/facility/dashboard/summary');
      return response.data.content || null;
    },
    enabled: !isProviderDashboard,
  });

  const activitiesQuery = useQuery({
    queryKey: ['dashboard', 'recent-activities'],
    queryFn: async () => {
      const response = await axios.get<ApiResponseEnvelope<ActivityItem[]>>('/v1/admin/facility/dashboard/recent-activities');
      return response.data.content || [];
    },
    enabled: !isProviderDashboard,
  });

  const providerOpportunitiesQuery = useQuery({
    queryKey: manproQueryKeys.providerOpportunities({ limit: 200 }),
    // Dashboard Provider memakai pipeline MANPRO, bukan summary admin facility.
    queryFn: () => providerOpportunityService.getProviderOpportunities({ limit: 200 }),
    enabled: isProviderDashboard,
  });

  const providerBidsQuery = useQuery({
    queryKey: manproQueryKeys.providerBids({ limit: 200 }),
    // Data bid menjadi sumber status: submitted, accepted, rejected, withdrawn, dan project MANPRO.
    queryFn: () => providerBidService.getProviderBids({ limit: 200 }),
    enabled: isProviderDashboard,
  });

  useEffect(() => {
    if (summaryQuery.error) toast.error('Gagal memuat ringkasan dashboard.');
  }, [summaryQuery.error]);

  useEffect(() => {
    if (activitiesQuery.error) toast.error('Gagal memuat aktivitas dashboard.');
  }, [activitiesQuery.error]);

  const summary = summaryQuery.data;
  const projectHealth = summary?.project?.health_score ?? 86;
  const projectProgress = summary?.project?.physical_progress ?? 68;
  const isLoading = summaryQuery.isLoading || activitiesQuery.isLoading || isTechniciansLoading;

  const canAccessProject = userRole === 'provider' || permissions.some((role) => role.includes('project'));

  const cards = useMemo(
    () => [
      {
        title: 'Teknisi Aktif',
        value: technicianStats.active,
        caption: `${technicianStats.total} total terdaftar`,
        icon: Users,
        tone: 'bg-blue-50 text-blue-700',
        path: '/teknisi',
      },
      {
        title: 'Manajemen Proyek',
        value: summary?.project?.active_projects ?? summary?.project?.active ?? 0,
        caption: `${projectProgress}% progres fisik`,
        icon: BarChart3,
        tone: 'bg-purple-50 text-purple-700',
        path: '/project-management',
      },
      {
        title: 'Task Cleaning',
        value: summary?.cleaning?.today ?? 0,
        caption: 'Pekerjaan hari ini',
        icon: CalendarCheck,
        tone: 'bg-emerald-50 text-emerald-700',
        path: '/cleaning',
      },
      {
        title: 'AC Pending',
        value: summary?.ac_maintenance?.pending ?? 0,
        caption: 'Butuh tindak lanjut',
        icon: Wind,
        tone: 'bg-amber-50 text-amber-700',
        path: '/ac',
      },
      {
        title: 'Procurement',
        value: summary?.procurement?.low_stock ?? 0,
        caption: 'Low stock alert',
        icon: Package,
        tone: 'bg-sky-50 text-sky-700',
        path: '/procurement',
      },
      {
        title: 'Laundry Aktif',
        value: summary?.laundry?.active ?? 0,
        caption: 'Order berjalan',
        icon: Wrench,
        tone: 'bg-rose-50 text-rose-700',
        path: '/laundry',
      },
    ],
    [projectProgress, summary, technicianStats.active, technicianStats.total],
  );

  const activities = activitiesQuery.data || [];

  const providerBidRows = useMemo(
    () => getPaginatedRows<ProviderBidDto>(providerBidsQuery.data).map(mapProviderBidToRow),
    [providerBidsQuery.data],
  );
  const providerSubmittedCount = providerBidRows.filter((bid) => bid.status === 'submitted' || bid.status === 'pending').length;
  const providerAcceptedCount = providerBidRows.filter((bid) => bid.status === 'accepted').length;
  const providerRejectedCount = providerBidRows.filter((bid) => bid.status === 'rejected' || bid.status === 'denied').length;
  const providerManproProjectCount = providerBidRows.filter((bid) => bid.status === 'accepted' && bid.manproProjectId).length;
  const providerDashboardLoading = providerOpportunitiesQuery.isLoading || providerBidsQuery.isLoading;

  if (isProviderDashboard) {
    const providerCards = [
      {
        title: 'Bid Terkirim',
        value: providerBidRows.length,
        caption: 'Total penawaran Anda',
        icon: Send,
        tone: 'bg-sky-50 text-sky-700',
        path: '/provider-bids',
      },
      {
        title: 'Menunggu Review',
        value: providerSubmittedCount,
        caption: 'Sedang direview Owner',
        icon: Clock,
        tone: 'bg-amber-50 text-amber-700',
        path: '/provider-bids',
      },
      {
        title: 'Bid Diterima',
        value: providerAcceptedCount,
        caption: 'Penawaran dimenangkan',
        icon: CheckCircle2,
        tone: 'bg-emerald-50 text-emerald-700',
        path: '/provider-bids',
      },
      {
        title: 'Bid Ditolak',
        value: providerRejectedCount,
        caption: 'Tidak terpilih Owner',
        icon: ShieldCheck,
        tone: 'bg-rose-50 text-rose-700',
        path: '/provider-bids',
      },
      {
        title: 'Project MANPRO',
        value: providerManproProjectCount,
        caption: 'Project hasil bid accepted',
        icon: Briefcase,
        tone: 'bg-purple-50 text-purple-700',
        path: '/project-management',
      },
    ];
    const recentBids = providerBidRows.slice(0, 5);

    return (
      <div className="space-y-6">
        <section className="overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 p-6 text-white shadow-xl shadow-blue-100">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold ring-1 ring-white/20">
                <Sparkles size={15} />
                Provider MANPRO Pipeline
              </span>
              <h1 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tight">
                Kelola peluang, bid, dan project MANPRO Anda dari satu dashboard.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-blue-50">
                Dashboard ini fokus untuk Provider: cari opportunity Owner, kirim bid, pantau status review, lalu lanjut ke project MANPRO saat penawaran diterima.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/provider-opportunities" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-blue-700 shadow-sm transition hover:bg-blue-50">
                  Lihat Peluang Provider
                  <ArrowRight size={17} />
                </Link>
                <Link to="/provider-bids" className="inline-flex items-center gap-2 rounded-xl bg-blue-950/20 px-5 py-3 text-sm font-extrabold text-white ring-1 ring-white/25 transition hover:bg-blue-950/30">
                  Lihat Penawaran Saya
                  <Send size={17} />
                </Link>
                <Link to="/project-management" className="inline-flex items-center gap-2 rounded-xl bg-blue-950/20 px-5 py-3 text-sm font-extrabold text-white ring-1 ring-white/25 transition hover:bg-blue-950/30">
                  Lihat Project MANPRO
                  <Briefcase size={17} />
                </Link>
              </div>
            </div>

            <div className="rounded-3xl bg-white/12 p-5 ring-1 ring-white/20 backdrop-blur">
              <p className="text-sm font-extrabold text-blue-50">Pipeline Bid Provider</p>
              <div className="mt-5 flex items-end justify-between">
                <div>
                  <p className="text-5xl font-extrabold">{providerDashboardLoading ? '...' : providerAcceptedCount}</p>
                  <p className="mt-2 text-sm font-semibold text-blue-50">Bid diterima Owner</p>
                </div>
                <CheckCircle2 size={52} className="text-blue-100" />
              </div>
              <div className="mt-5 h-3 rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-white"
                  style={{ width: `${Math.min(100, providerBidRows.length ? (providerAcceptedCount / providerBidRows.length) * 100 : 0)}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        {(providerOpportunitiesQuery.isError || providerBidsQuery.isError) && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-5 text-sm font-semibold text-rose-700">
            Gagal memuat sebagian data Provider MANPRO. Pastikan akun Anda memiliki akses provider.
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {providerCards.map((card) => {
            const Icon = card.icon;

            return (
              <Link key={card.title} to={card.path} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-extrabold text-slate-700">{card.title}</p>
                    <p className="mt-3 text-3xl font-extrabold text-slate-900">{providerDashboardLoading ? '...' : card.value}</p>
                    <p className="mt-2 text-xs font-semibold text-slate-500">{card.caption}</p>
                  </div>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full ${card.tone}`}>
                    <Icon size={23} />
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Status Penawaran Terbaru</h2>
                <p className="mt-1 text-sm text-slate-500">Pantau bid terakhir yang dikirim ke Owner.</p>
              </div>
              <TrendingUp size={24} className="text-blue-600" />
            </div>

            <div className="mt-6 space-y-3">
              {recentBids.length ? (
                recentBids.map((bid) => (
                  <div key={bid.id} className="rounded-2xl border border-slate-100 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-slate-900">{bid.title}</p>
                        <p className="mt-1 text-xs font-semibold text-slate-500">{bid.location} • {bid.amountLabel}</p>
                      </div>
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700">
                        {bid.statusLabel}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center">
                  <FileText size={36} className="mx-auto text-slate-300" />
                  <p className="mt-3 font-bold text-slate-700">Belum ada bid terkirim</p>
                  <p className="mt-1 text-sm text-slate-400">Mulai dari halaman Peluang Provider.</p>
                </div>
              )}
            </div>
          </div>

          <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Aksi Berikutnya</h2>
            <div className="mt-5 space-y-3">
              {[
                ['Cari opportunity baru', 'Temukan proyek Owner yang sesuai kapasitas Anda.', '/provider-opportunities', Building2],
                ['Pantau penawaran', 'Lihat bid yang menunggu review atau sudah diterima.', '/provider-bids', Send],
                ['Masuk MANPRO', 'Kelola project yang sudah dimenangkan.', '/project-management', Briefcase],
              ].map(([title, description, path, Icon]) => (
                <Link key={title as string} to={path as string} className="block rounded-2xl border border-slate-100 p-4 transition hover:border-blue-100 hover:bg-blue-50">
                  <Icon size={21} className="text-blue-600" />
                  <p className="mt-2 text-sm font-extrabold text-slate-900">{title as string}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">{description as string}</p>
                </Link>
              ))}
            </div>
          </aside>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 p-6 text-white shadow-xl shadow-blue-100">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold ring-1 ring-white/20">
              <Sparkles size={15} />
              Facility & Project Marketplace
            </span>
            <h1 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tight">
              Dashboard operasional untuk fasilitas, vendor, teknisi, dan proyek.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-blue-50">
              Pantau performa teknisi, status pekerjaan, progress proyek MANPRO, dan opportunity provider dalam satu tampilan modern.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/provider-opportunities" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-blue-700 shadow-sm transition hover:bg-blue-50">
                Lihat Peluang Provider
                <ArrowRight size={17} />
              </Link>
              {canAccessProject && (
                <Link to="/project-management" className="inline-flex items-center gap-2 rounded-xl bg-blue-950/20 px-5 py-3 text-sm font-extrabold text-white ring-1 ring-white/25 transition hover:bg-blue-950/30">
                  Buka MANPRO
                  <Briefcase size={17} />
                </Link>
              )}
              <button 
                onClick={() => { throw new Error("Sentry Test Error: Hello from Admin Dashboard!"); }}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-rose-600"
              >
                Tes Error Sentry
              </button>
            </div>
          </div>

          <div className="rounded-3xl bg-white/12 p-5 ring-1 ring-white/20 backdrop-blur">
            <p className="text-sm font-extrabold text-blue-50">Health Score Operasional</p>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <p className="text-5xl font-extrabold">{projectHealth}%</p>
                <p className="mt-2 text-sm font-semibold text-blue-50">Kondisi sistem optimal</p>
              </div>
              <ShieldCheck size={52} className="text-blue-100" />
            </div>
            <div className="mt-5 h-3 rounded-full bg-white/20">
              <div className="h-full rounded-full bg-white" style={{ width: `${projectHealth}%` }} />
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <Link key={card.title} to={card.path} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-extrabold text-slate-700">{card.title}</p>
                  <p className="mt-3 text-3xl font-extrabold text-slate-900">{isLoading ? '...' : card.value}</p>
                  <p className="mt-2 text-xs font-semibold text-slate-500">{card.caption}</p>
                </div>
                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${card.tone}`}>
                  <Icon size={23} />
                </div>
              </div>
            </Link>
          );
        })}
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">Progress MANPRO</h2>
              <p className="mt-1 text-sm text-slate-500">Realisasi fisik terhadap target baseline.</p>
            </div>
            <TrendingUp size={24} className="text-blue-600" />
          </div>
          <div className="mt-7">
            <div className="flex items-end justify-between">
              <p className="text-5xl font-extrabold text-slate-900">{projectProgress}%</p>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-extrabold text-emerald-700">
                On Track
              </span>
            </div>
            <div className="mt-5 h-4 rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-blue-600" style={{ width: `${projectProgress}%` }} />
            </div>
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              {['Planning', 'Execution', 'Validation'].map((stage, index) => (
                <div key={stage} className="rounded-2xl border border-slate-100 p-4">
                  <CheckCircle2 size={19} className={index < 2 ? 'text-emerald-600' : 'text-blue-600'} />
                  <p className="mt-2 text-sm font-extrabold text-slate-800">{stage}</p>
                  <p className="mt-1 text-xs text-slate-500">{index < 2 ? 'Berjalan baik' : 'Menunggu approval'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-extrabold text-slate-900">Aktivitas Terkini</h2>
          <div className="mt-5 space-y-4">
            {activities.length > 0 ? (
              activities.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                    <Clock size={17} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-800">{activity.task_name || activity.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {activity.team || activity.by} • {activity.time || (activity.created_at ? new Date(activity.created_at).toLocaleDateString() : '')}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <FileText className="mb-2 h-8 w-8 text-slate-300" />
                <p className="text-sm font-semibold text-slate-600">Belum ada aktivitas</p>
                <p className="text-xs text-slate-400">Aktivitas terkini akan muncul di sini</p>
              </div>
            )}
          </div>
        </aside>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {[
          ['Vendor marketplace', 'Kelola bid dan opportunity provider.', '/provider-opportunities', Building2],
          ['Teknisi lapangan', 'Pantau kapasitas dan status teknisi.', '/teknisi', Users],
          ['Project control', 'Masuk ke lifecycle MANPRO.', '/project-management', Briefcase],
        ].map(([title, description, path, Icon]) => (
          <Link key={title as string} to={path as string} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
            <Icon size={24} className="text-blue-600" />
            <p className="mt-3 font-extrabold text-slate-900">{title as string}</p>
            <p className="mt-2 text-sm leading-6 text-slate-500">{description as string}</p>
          </Link>
        ))}
      </section>
    </div>
  );
};

export default Dashboard;
